import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { SegmentedControl } from '@client/components/_ui/data-entry/SegmentedControl';
import { confirm } from '@client/components/_ui/feedback/confirm-modal';
import { errorNotification } from '@client/components/_ui/feedback/ErrorNotification';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { sleep } from '@client/utils';
import { cn } from '@client/utils/styling/cn';
import { DEFAULT_MAX_ACCESSIBLE_RECORDS, DEFAULT_PAGE_SIZE } from '@shared/constants';
import { PERMISSIONS } from '@shared/permissions/permissions';
import type { S3File } from '@shared/types';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Empty } from 'antd';
import type { Key } from 'antd/es/table/interface';
import { Inbox as InboxIcon, Mail, MessageSquare, Zap } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AddMessagesButton } from './AddMessagesButton';
import { useEmailClassificationColumns } from './columns';
import { EmailClassificationFilters } from './filters';
import { type InboxFilterKey, InboxQueueCards } from './InboxQueueCards';
import { useInboxNavigationActions } from './inbox-navigation-store';
import { SmsUploadModal } from './SmsUploadModal';

export const Route = createFileRoute('/console/$orgId/tools/auto-detect/inbox')({
    staticData: {
        breadcrumb: {
            title: 'Inbox',
        },
        ui: {
            icon: Zap,
            title: 'Auto-detect inbox',
            subtitle: 'Review and action messages flagged by the AI classifier',
        },
        modules: ['auto-detect'],
        permissions: [PERMISSIONS.MESSAGE_CLASSIFICATION_ACCESS],
    },
    component: Inbox,
});

type QueryParams = RouterInputs['messageClassification']['listMessageClassification'];
type EmailClassificationWhere = NonNullable<QueryParams['where']>;
type EmailClassificationData = RouterOutputs['messageClassification']['listMessageClassification'];
type EmailClassificationRow = NonNullable<EmailClassificationData>['rows'][number];
type InboxCounts = RouterOutputs['messageClassification']['getInboxCounts'];

export type MessageTypeFilter = 'ALL' | 'EMAIL' | 'SMS';

const INBOX_FILTER_LABELS = {
    all: null,
    needsReview: 'Needs review',
    highPriority: 'High priority',
    dualClassified: 'Multi-classified',
    vulnerabilities: 'Vulnerabilities',
    breaches: 'Breaches',
    errors: 'Errors',
    totalUnreviewed: 'Unreviewed',
    reviewed: 'Reviewed',
} as const satisfies Record<InboxFilterKey, string | null>;

/** Determines the best default filter based on severity cascade: breaches → high priority → needs review → all */
function computeSmartDefault(counts: InboxCounts): InboxFilterKey {
    if (counts.breaches > 0) return 'breaches';
    if (counts.highPriority > 0) return 'highPriority';
    if (counts.needsReview > 0) return 'needsReview';
    return 'all';
}

/**
 * Maps a filter key to a Prisma where clause.
 *
 * MUST stay in lockstep with the server's chip aggregate
 * (`messageClassification_getInboxCounts.sql`) or a chip number will disagree
 * with the table it filters. CUR-849 item 4 aligned SKIPPED semantics: every
 * attention chip excludes both REVIEWED and reviewer-SKIPPED rows.
 */
function getFilterWhere(key: InboxFilterKey): EmailClassificationWhere | undefined {
    switch (key) {
        case 'all':
            return undefined;
        case 'needsReview':
            return { status: 'PROCESSED' };
        case 'highPriority':
            return {
                confidence: 'HIGH',
                OR: [{ isIncident: true }, { isComplaint: true }],
                status: { notIn: ['REVIEWED', 'SKIPPED'] },
            };
        case 'dualClassified':
            return { isIncident: true, isComplaint: true, status: { notIn: ['REVIEWED', 'SKIPPED'] } };
        case 'vulnerabilities':
            return {
                MessageVulnerabilities: { some: { reviewStatus: { not: 'DISMISSED' } } },
                status: { notIn: ['REVIEWED', 'SKIPPED'] },
            };
        case 'breaches':
            return { isBreachOfRegulation: true, status: { notIn: ['REVIEWED', 'SKIPPED'] } };
        case 'errors':
            return { status: 'ERROR' };
        case 'totalUnreviewed':
            return { status: { notIn: ['REVIEWED', 'SKIPPED'] } };
        case 'reviewed':
            return { status: 'REVIEWED' };
    }
}

/** Returns row className based on priority hierarchy (first match wins) */
function getRowClassName(record: EmailClassificationRow): string {
    // Mirrors the chip semantics: reviewer-skipped rows are settled, not
    // attention-worthy (CUR-849 item 4).
    const isUnreviewed = record.status !== 'REVIEWED' && record.status !== 'SKIPPED';

    // 1. Breach + unreviewed
    if (record.isBreachOfRegulation && isUnreviewed) {
        return 'bg-red-50/50 dark:bg-red-950/30';
    }

    // 2. High confidence incident/complaint + unreviewed
    if (record.confidence === 'HIGH' && (record.isIncident || record.isComplaint) && isUnreviewed) {
        return 'bg-orange-50/50 dark:bg-orange-950/30';
    }

    // 3. Error status
    if (record.status === 'ERROR') {
        return 'bg-warning-subtle/50';
    }

    // 4. Reviewed / skipped — settled, muted
    if (record.status === 'REVIEWED' || record.status === 'SKIPPED') {
        return 'opacity-60';
    }

    return '';
}

function Inbox() {
    const { trpcUtils, trpc } = useTrpc();
    const orgId = useOrgId();
    const { mutateAsync: deleteMessageClassification } =
        trpc.messageClassification.deleteMessageClassification.useMutation();
    const { mutateAsync: rerunClassification } = trpc.messageClassification.rerunClassification.useMutation();
    const { mutateAsync: processEmail } = trpc.messageClassification.processEmailFile.useMutation();
    const { mutate: quickReview } = trpc.messageClassification.quickReviewMessageClassification.useMutation({
        onSuccess() {
            message.success('Message marked as reviewed.');
            trpcUtils.messageClassification.invalidate();
        },
        onError(error) {
            errorMessage.show(error);
        },
    });
    const { mutate: bulkReview } = trpc.messageClassification.bulkQuickReview.useMutation({
        onSuccess(data) {
            message.success(`${data.updatedCount} message(s) marked as reviewed.`);
            trpcUtils.messageClassification.invalidate();
            setSelectedRowKeys([]);
        },
        onError(error) {
            errorMessage.show(error);
        },
    });

    const [queryParams, setQueryParams] = usePathState<QueryParams>('query-params', {
        limit: DEFAULT_PAGE_SIZE,
        offset: 0,
    });

    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
    const [activeFilter, setActiveFilter] = useState<InboxFilterKey | null>(null);
    const [messageTypeFilter, setMessageTypeFilter] = useState<MessageTypeFilter>('ALL');
    const [smsUploadOpen, setSmsUploadOpen] = useState(false);

    // Track whether a programmatic filter is active to avoid FilterBar sync loops
    const isProgrammaticFilterRef = useRef(false);
    // Track whether the smart default has been applied (prevents overriding user interaction)
    const hasInitializedFilterRef = useRef(false);

    // Counts query — used to compute smart default filter on first load
    const countsQuery = trpc.messageClassification.getInboxCounts.useQuery(
        messageTypeFilter !== 'ALL' ? { messageType: messageTypeFilter } : undefined,
        { refetchInterval: 30_000 },
    );

    // Merge messageType into query params when a specific type is selected
    const effectiveQueryParams = useMemo(() => {
        if (messageTypeFilter === 'ALL') return queryParams;
        return {
            ...queryParams,
            where: {
                ...queryParams.where,
                messageType: messageTypeFilter,
            },
        };
    }, [queryParams, messageTypeFilter]);

    // Defer table query until smart default is determined
    const { data, isLoading, refetch } = trpc.messageClassification.listMessageClassification.useQuery(
        effectiveQueryParams,
        {
            refetchInterval: 1000 * 5,
            enabled: activeFilter !== null,
        },
    );

    // Sync visible rows to the navigation store for the drawer's prev/next and its "Next: …"
    // preview (id + subject, so the drawer needs no second request).
    const { setVisibleRows, clearVisibleRows, setFilterLabel } = useInboxNavigationActions();
    useEffect(() => {
        setVisibleRows(data?.rows ?? []);
        return () => clearVisibleRows();
    }, [data?.rows, setVisibleRows, clearVisibleRows]);

    // Publish the active filter label so the drawer's pager can render
    // "X of Y in <filter>" — keeps the scope explicit when the reviewer
    // is paging through a non-default slice.
    useEffect(() => {
        const label = activeFilter ? INBOX_FILTER_LABELS[activeFilter] : null;
        setFilterLabel(label);
        return () => setFilterLabel(null);
    }, [activeFilter, setFilterLabel]);

    // Apply smart default filter on first counts load (Option B: deferred table query)
    const smartDefault = useMemo(
        () => (countsQuery.data ? computeSmartDefault(countsQuery.data) : null),
        [countsQuery.data],
    );
    useEffect(() => {
        if (hasInitializedFilterRef.current) return;
        if (smartDefault) {
            hasInitializedFilterRef.current = true;
            setActiveFilter(smartDefault);
            isProgrammaticFilterRef.current = true;
            const where = getFilterWhere(smartDefault);
            setQueryParams((prev) => ({ ...prev, where, offset: 0 }));
        } else if (countsQuery.isError) {
            // Fallback to 'all' if counts query fails
            hasInitializedFilterRef.current = true;
            setActiveFilter('all');
        }
    }, [smartDefault, countsQuery.isError, setQueryParams]);

    const handleFilterChange = useCallback(
        (key: InboxFilterKey) => {
            hasInitializedFilterRef.current = true;
            setActiveFilter(key);
            isProgrammaticFilterRef.current = true;
            const where = getFilterWhere(key);
            setQueryParams((prev) => ({ ...prev, where, offset: 0 }));
        },
        [setQueryParams],
    );

    const handleFilterBarChange = useCallback(
        (where: EmailClassificationWhere) => {
            hasInitializedFilterRef.current = true;
            // When user manually uses FilterBar, reset the active filter
            if (!isProgrammaticFilterRef.current) {
                setActiveFilter('all');
            }
            isProgrammaticFilterRef.current = false;
            setQueryParams((prev) => ({ ...prev, where, offset: 0 }));
        },
        [setQueryParams],
    );

    async function handleRerunClassification(record: EmailClassificationRow) {
        try {
            message.loading('Rerunning classification. Please wait...');
            void rerunClassification({
                messageClassificationId: record.id,
            });
            await sleep(1000);
            trpcUtils.messageClassification.invalidate();
        } catch (error) {
            message.destroy();
            errorMessage.show(error);
        }
    }

    async function handleDeleteEmail(messageClassificationId: bigint) {
        try {
            message.loading('Deleting email classification...', 0);

            await deleteMessageClassification({ messageClassificationId });
            await refetch();

            message.success('Email classification deleted successfully.');
        } catch (error) {
            message.destroy();
            errorMessage.show(error);
        }
    }

    function handleQuickReview(record: EmailClassificationRow) {
        quickReview({ messageClassificationId: record.id });
    }

    async function handleProcessEmail(uploadedFile: S3File) {
        const hideLoading = message.loading('Uploading email...', 0);
        try {
            const result = await processEmail({
                fileKey: uploadedFile.key,
            });
            if (!result.success) {
                errorNotification(result.error);
                return;
            }
            await sleep(1000);
            await refetch();

            message.success('Email uploaded successfully. It will be processed in the background.');
        } catch {
            message.destroy();
            errorMessage.show('Unable to import email. Please try again.');
        } finally {
            hideLoading();
        }
    }

    async function handleProcessSelected() {
        message.loading('Processing emails. Please wait...');
        await Promise.all(
            selectedRowKeys.map((key) =>
                rerunClassification({
                    messageClassificationId: BigInt(key),
                }),
            ),
        );
        await refetch();
        setSelectedRowKeys([]);
    }

    async function handleDeleteSelected() {
        message.loading('Deleting emails. Please wait...');
        await Promise.all(
            selectedRowKeys.map((key) => deleteMessageClassification({ messageClassificationId: BigInt(key) })),
        );
        await refetch();
        message.success('Emails deleted successfully.');
        setSelectedRowKeys([]);
    }

    function handleBulkReview() {
        const ids = selectedRowKeys.map((key) => BigInt(key));
        bulkReview({ messageClassificationIds: ids });
    }

    const columns = useEmailClassificationColumns({
        onRerunClassification: handleRerunClassification,
        onDeleteEmail: handleDeleteEmail,
        onQuickReview: handleQuickReview,
    });

    const navigate = useNavigate();

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isLoading || activeFilter === null,
        columns,
        rowKey: (row) => String(row.id),
        queryParams: queryParams,
        onParamsChange: setQueryParams,
        pagination: {
            position: 'both',
            maxAccessibleRecords: DEFAULT_MAX_ACCESSIBLE_RECORDS,
        },
        columnVisibility: {
            enabled: true,
            // v2 key — the command-centre layout has a different column set,
            // so preferences saved against the old layout must not be spliced
            // in by the column-preference merger.
            persistKey: 'email-classification-table-columns-v2',
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
        rowSelection: {
            enabled: true,
            type: 'checkbox',
            preserveSelectedRowKeys: true,
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
        },
        showSorterTooltip: false,
    });

    const hasSelected = selectedRowKeys.length > 0;

    return (
        <PageController route={Route}>
            <Outlet />

            <InboxQueueCards
                activeFilter={activeFilter}
                onFilterChange={handleFilterChange}
                messageType={messageTypeFilter !== 'ALL' ? messageTypeFilter : undefined}
            />

            <TableToolbar
                variant="enhanced"
                filters={<EmailClassificationFilters onChange={handleFilterBarChange} />}
                actions={
                    <Space>
                        <SegmentedControl
                            value={messageTypeFilter}
                            onChange={setMessageTypeFilter}
                            options={[
                                { label: 'All', value: 'ALL' as const, icon: <InboxIcon size={14} /> },
                                { label: 'Emails', value: 'EMAIL' as const, icon: <Mail size={14} /> },
                                { label: 'SMS', value: 'SMS' as const, icon: <MessageSquare size={14} /> },
                            ]}
                            variant="bordered"
                            size="middle"
                        />
                        <AddMessagesButton
                            onEmailUploaded={handleProcessEmail}
                            onUploadSms={() => setSmsUploadOpen(true)}
                        />
                    </Space>
                }
            />

            {hasSelected && (
                <div className="mb-3 flex items-center gap-2 rounded-lg border border-accent-100 bg-accent-25 py-1.5 pr-2 pl-3.5">
                    <Button
                        size="small"
                        onClick={() => {
                            confirm({
                                title: 'Mark as reviewed',
                                content: `Are you sure you want to mark ${selectedRowKeys.length} message(s) as reviewed?`,
                                okText: 'Mark as reviewed',
                                onOk: handleBulkReview,
                            });
                        }}
                    >
                        Mark as reviewed
                    </Button>
                    <Button size="small" onClick={handleProcessSelected}>
                        Re-process
                    </Button>
                    <Button
                        size="small"
                        type="danger"
                        onClick={() => {
                            confirm({
                                title: 'Delete selected',
                                content: `Are you sure you want to delete ${selectedRowKeys.length} item(s)?`,
                                okText: 'Delete',
                                okDanger: true,
                                onOk: handleDeleteSelected,
                            });
                        }}
                    >
                        Delete
                    </Button>
                </div>
            )}

            <PrismaTable
                {...tableProps}
                locale={{
                    emptyText: (
                        <Empty
                            description={
                                <div className="space-y-2">
                                    <p className="font-medium text-base text-muted-foreground">
                                        No messages to classify
                                    </p>
                                    <p className="text-sm text-subtle-foreground">
                                        Upload email or SMS files to automatically detect and classify compliance issues
                                    </p>
                                </div>
                            }
                        />
                    ),
                }}
                size="small"
                rowClassName={(record) => cn('cursor-pointer', getRowClassName(record as EmailClassificationRow))}
                onRow={({ id }) => ({
                    onClick(e) {
                        const target = e.target as HTMLElement;
                        // .btn-variant covers ButtonModern buttons (Review, kebab),
                        // which render plain <button>s without antd's .ant-btn class.
                        if (target.closest('.ant-btn, .ant-checkbox-wrapper, .btn-variant')) return;
                        navigate({
                            to: '/console/$orgId/tools/auto-detect/inbox/$id/review-email-classification',
                            params: { orgId, id },
                        });
                    },
                })}
            />

            <SmsUploadModal
                open={smsUploadOpen}
                onClose={() => setSmsUploadOpen(false)}
                onSuccess={() => {
                    setSmsUploadOpen(false);
                    refetch();
                    trpcUtils.messageClassification.invalidate();
                }}
            />
        </PageController>
    );
}
